<app-main-layout>
  <section class="minio-page" aria-labelledby="minio-title">
    <header class="page-heading">
      <div>
        <span class="eyebrow">Интеграции</span>
        <h1 id="minio-title">MinIO</h1>
        <p>S3-хранилище для файлов dashboard, билетов, логотипов и служебных вложений.</p>
      </div>

      <button class="refresh-button" type="button" (click)="loadStatus()" [disabled]="loading">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M16 8h5V3"/></svg>
        <span>{{ loading ? 'Проверяем' : 'Обновить' }}</span>
      </button>
    </header>

    <div class="error-state" *ngIf="errorMessage">
      {{ errorMessage }}
    </div>

    <section class="summary-grid" *ngIf="status as data">
      <article class="summary-card" [class]="'summary-card tone-' + statusTone">
        <span>Статус</span>
        <strong>{{ statusLabel }}</strong>
        <small>Проверено {{ checkedAtLabel }}</small>
      </article>

      <article class="summary-card tone-blue">
        <span>Bucket</span>
        <strong>{{ data.bucket || 'Не задан' }}</strong>
        <small>{{ data.region }}</small>
      </article>

      <article class="summary-card tone-violet">
        <span>S3 endpoint</span>
        <strong>{{ data.publicEndpoint ? 'Настроен' : 'Не задан' }}</strong>
        <small>{{ protectedChecksLabel }}</small>
      </article>
    </section>

    <section class="endpoint-strip" *ngIf="status as data">
      <a class="endpoint-link" *ngIf="data.publicEndpoint" [href]="data.publicEndpoint" target="_blank" rel="noreferrer">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
        <span>S3</span>
      </a>

      <a class="endpoint-link" *ngIf="data.consoleUrl" [href]="data.consoleUrl" target="_blank" rel="noreferrer">
        <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 8h10"/><path d="M7 12h5"/><path d="M7 16h8"/></svg>
        <span>Console</span>
      </a>
    </section>

    <section class="access-panel" *ngIf="status as data">
      <header class="panel-heading">
        <div>
          <span class="eyebrow">Access</span>
          <h2>Доступ для загрузки</h2>
        </div>
      </header>

      <div class="access-notice" [ngClass]="'state-' + viewerAccessTone">
        <strong>{{ viewerAccessTitle }}</strong>
        <span>{{ viewerAccessDescription }}</span>
      </div>

      <div class="access-grid">
        <article class="access-card">
          <span>MinIO Console</span>
          <a *ngIf="data.access.consoleUrl; else noConsoleUrl" [href]="data.access.consoleUrl" target="_blank" rel="noreferrer">
            {{ data.access.consoleUrl }}
          </a>
          <ng-template #noConsoleUrl>
            <code>Не задан</code>
          </ng-template>
          <small>Открывать с IP {{ data.access.allowedIp || '--' }}</small>
        </article>

        <article class="access-card">
          <span>S3 endpoint</span>
          <a *ngIf="data.access.s3Endpoint; else noS3Endpoint" [href]="data.access.s3Endpoint" target="_blank" rel="noreferrer">
            {{ data.access.s3Endpoint }}
          </a>
          <ng-template #noS3Endpoint>
            <code>Не задан</code>
          </ng-template>
          <small>Для S3-клиентов и загрузки файлов</small>
        </article>

        <article class="access-card">
          <span>Bucket / Region</span>
          <code>{{ data.access.bucket || 'Не задан' }}</code>
          <small>{{ data.access.region }}</small>
        </article>

        <article class="access-card">
          <span>Login / Access key</span>
          <code>{{ data.access.uploaderAccessKeyId || 'Не задан' }}</code>
          <small>Используется для MinIO Console и S3-клиентов</small>
        </article>

        <article class="access-card">
          <span>Secret key</span>
          <code>{{ data.access.secretKeyAvailable ? 'Задан' : 'Не задан' }}</code>
          <small>{{ data.access.secretKeyReference }}</small>
        </article>

        <article class="access-card">
          <span>IP allowlist</span>
          <code>{{ data.access.allowedIp || 'Не задан' }}</code>
          <small>Dashboard видит вас как {{ data.access.viewerIp || '--' }}</small>
        </article>
      </div>
    </section>

    <section class="health-panel" *ngIf="status as data">
      <header class="panel-heading">
        <div>
          <span class="eyebrow">Health</span>
          <h2>Проверки подключения</h2>
        </div>
      </header>

      <div class="health-grid">
        <article
          class="health-card"
          *ngFor="let check of data.checks; trackBy: trackByCheckId"
          [ngClass]="'state-' + check.status"
        >
          <div class="health-card-head">
            <strong>{{ check.label }}</strong>
            <span>{{ getCheckLabel(check.status) }}</span>
          </div>

          <code>{{ check.url || 'URL не задан' }}</code>

          <div class="health-meta">
            <span>HTTP {{ check.statusCode || '--' }}</span>
            <span>{{ check.latencyMs === null ? '--' : check.latencyMs + ' мс' }}</span>
          </div>

          <p>{{ check.detail }}</p>
        </article>
      </div>
    </section>

    <section class="details-grid" *ngIf="status as data">
      <article class="config-panel">
        <header class="panel-heading">
          <div>
            <span class="eyebrow">Config</span>
            <h2>Текущие параметры</h2>
          </div>
        </header>

        <div class="config-list">
          <div class="config-row" *ngFor="let item of data.configuration; trackBy: trackByConfigurationLabel">
            <div>
              <span>{{ item.label }}</span>
              <code>{{ item.value }}</code>
            </div>
            <strong [class]="'state-' + item.state">{{ getConfigStateLabel(item) }}</strong>
          </div>
        </div>
      </article>

      <article class="steps-panel">
        <header class="panel-heading">
          <div>
            <span class="eyebrow">Next</span>
            <h2>Очередь подключения</h2>
          </div>
        </header>

        <div class="step-list">
          <div class="step-row" *ngFor="let step of nextSteps; trackBy: trackByStepTitle" [ngClass]="'state-' + step.state">
            <span></span>
            <div>
              <strong>{{ step.title }}</strong>
              <p>{{ step.description }}</p>
            </div>
          </div>
        </div>
      </article>
    </section>

    <section class="loading-panel" *ngIf="loading && !status">
      Проверяем MinIO...
    </section>
  </section>
</app-main-layout>
